import { motion } from "framer-motion";
import Reveal from "@/components/Reveal";
import { SIMULATOR_URL } from "@/components/Nav";
import { track } from "@/lib/track";

/* 28x28 intensity map encoding a digit "7" (two strokes + texture) */
const CELLS = (() => {
  const out = [];
  for (let r = 0; r < 28; r++) {
    for (let c = 0; c < 28; c++) {
      let i = 0;
      if (c >= 5 && c <= 22) i = Math.max(i, Math.max(0, 1 - Math.abs(r - 6.5) / 2.2));
      if (r >= 8 && r <= 23) {
        const cx = 21 - (13 * (r - 8)) / 15;
        i = Math.max(i, Math.max(0, 1 - Math.abs(c - cx) / 2.0));
      }
      const tex = 0.8 + 0.2 * (((r * 7 + c * 13) % 10) / 10);
      out.push(Math.min(1, i * tex));
    }
  }
  return out;
})();

function EmitterGrid() {
  return (
    <div
      className="grid w-full max-w-[250px] gap-[2px]"
      style={{ gridTemplateColumns: "repeat(28, minmax(0,1fr))" }}
      data-testid="mockup-emitter-grid"
    >
      {CELLS.map((v, idx) => (
        <span
          key={idx}
          className="aspect-square w-full rounded-full"
          style={{
            backgroundColor:
              v < 0.06 ? "rgba(255,255,255,0.05)" : `rgba(${Math.round(120 + 135 * v)}, ${Math.round(170 + 85 * v)}, 255, ${(0.08 + 0.92 * v).toFixed(2)})`,
            boxShadow: v > 0.75 ? "0 0 6px rgba(0,229,255,0.55)" : undefined,
          }}
        />
      ))}
    </div>
  );
}

function Connector({ horizontal = false }) {
  const pulse = horizontal
    ? { animate: { left: ["0%", "calc(100% - 8px)"] } }
    : { animate: { top: ["0%", "calc(100% - 8px)"] } };
  return (
    <div className={`relative ${horizontal ? "h-px w-10" : "h-8 w-px"} bg-gradient-to-r ${horizontal ? "from-transparent via-beam/60 to-transparent" : "via-beam/60 from-transparent to-transparent"}`}>
      <motion.span
        {...pulse}
        transition={{ repeat: Infinity, duration: 1.6, ease: "linear" }}
        className="absolute rounded-full bg-beam shadow-[0_0_8px_rgba(0,229,255,0.9)]"
        style={horizontal ? { top: -3, width: 8, height: 8 } : { left: -3, width: 8, height: 8 }}
      />
    </div>
  );
}

function TransferCurve() {
  return (
    <div className="mt-2 flex items-center gap-2">
      <svg viewBox="0 0 64 22" className="h-6 w-16 shrink-0" fill="none" aria-hidden="true">
        <path d="M2 20 L2 2 M2 20 L62 20" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />
        <path d="M2 3 C 16 3, 18 19, 62 19" stroke="#00E5FF" strokeWidth="1.5" />
      </svg>
      <span className="font-mono text-[9px] leading-tight text-zinc-500">cos²(π·w/2 + φ)</span>
    </div>
  );
}

function Stage({ title, sub, children }) {
  return (
    <div className="flex-1 rounded-lg border border-white/10 bg-black/40 p-3">
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-zinc-200">{title}</p>
      <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.1em] text-beam">{sub}</p>
      {children}
    </div>
  );
}

const BARS = [4, 3, 5, 3, 4, 6, 4, 3, 100, 5];

function AccuracyBars() {
  return (
    <div className="mt-4" data-testid="mockup-bar-chart">
      <p className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-600">Class probabilities</p>
      <div className="flex h-24 items-end gap-1.5">
        {BARS.map((h, i) => (
          <div key={i} className="flex h-full flex-1 items-end">
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: `${h}%` }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: 0.05 * i, ease: [0.16, 1, 0.3, 1] }}
              className="w-full rounded-t-sm"
              style={
                i === 7
                  ? { background: "linear-gradient(180deg, #ffffff 0%, #00E5FF 100%)", boxShadow: "0 0 14px rgba(0,229,255,0.6)" }
                  : { background: "rgba(255,255,255,0.10)" }
              }
            />
          </div>
        ))}
      </div>
      <div className="mt-1.5 flex gap-1.5">
        {BARS.map((_, i) => (
          <span key={i} className={`flex-1 text-center font-mono text-[9px] ${i === 7 ? "font-bold text-beam" : "text-zinc-600"}`}>
            {i}
          </span>
        ))}
      </div>
    </div>
  );
}

const PILLS = ["EOM BIT PRECISION: 8-BIT", "SHOT NOISE: 0.010σ", "LASER RIN: 0.020σ", "THERMAL DRIFT: 0.030σ"];

export default function ProductPreviewSection() {
  return (
    <section id="preview" className="relative pb-28 md:pb-40" data-testid="preview-section">
      <div className="container-x">
        <Reveal>
          <p className="eyebrow mb-6 text-beam">{"// The Simulator — Live"}</p>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="clip-frame-sm overflow-hidden border border-white/10 bg-panel">
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-laser/80" />
                <span className="h-2.5 w-2.5 rounded-full bg-zinc-600" />
                <span className="h-2.5 w-2.5 rounded-full bg-beam/80" />
                <span className="ml-3 hidden font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 md:inline">
                  QPI Photonic Inference Simulator — session: MNIST · Linear
                </span>
              </div>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-laser" data-testid="mockup-live-badge">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-laser opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-laser" />
                </span>
                Live
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4 p-4 md:p-6 lg:grid-cols-[250px_1fr_270px]">
              <div className="rounded-lg border border-white/10 bg-black/40 p-4">
                <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">Emitter array 28×28 — MNIST 7</p>
                <div className="flex justify-center lg:justify-start">
                  <EmitterGrid />
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-black/40 p-4">
                <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">Photonic pipeline</p>
                <div className="flex flex-col items-stretch lg:flex-row lg:items-center">
                  <Stage title="VCSEL Array" sub="narrow-line · coherent" />
                  <div className="flex justify-center lg:hidden"><Connector /></div>
                  <div className="hidden justify-center lg:flex"><Connector horizontal /></div>
                  <Stage title="MZM Weight Matrix W" sub="thin-film LiNbO₃">
                    <TransferCurve />
                  </Stage>
                  <div className="flex justify-center lg:hidden"><Connector /></div>
                  <div className="hidden justify-center lg:flex"><Connector horizontal /></div>
                  <Stage title="Photodetector" sub="Σᵢ xᵢ·T(wᵢⱼ)" />
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-black/40 p-4">
                <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-600">Readout</p>
                <div className="space-y-2 font-mono text-[10px] uppercase tracking-[0.12em]">
                  <p className="text-zinc-500">Model: <span className="text-zinc-200">MNIST · Linear</span></p>
                  <p className="text-zinc-500">Photonic accuracy: <span className="font-bold text-beam">92.6%</span></p>
                  <p className="text-zinc-500">Ideal accuracy: <span className="font-bold text-zinc-200">98.1%</span></p>
                </div>
                <AccuracyBars />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-t border-white/10 px-4 py-4 md:px-6" data-testid="mockup-param-pills">
              {PILLS.map((pill) => {
                const [label, value] = pill.split(": ");
                return (
                  <span key={pill} className="rounded-full border border-white/10 bg-black/40 px-3 py-1.5 font-mono text-[10px] text-zinc-500">
                    {label}: <span className="text-zinc-200">{value}</span>
                  </span>
                );
              })}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="mt-6 max-w-2xl text-sm font-light leading-relaxed text-zinc-500">
            Run real neural-network inference on a photonic-compute abstraction — tune noise,
            quantization, and hardware parameters in real time.
          </p>
          <a
            href={SIMULATOR_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("simulator_launch")}
            data-testid="preview-launch-simulator"
            className="btn-laser mt-8"
          >
            Launch Simulator
            <span aria-hidden="true">↗</span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}
